import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Typography,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import { validateDocuments } from "./requireddocuments.helper";
import Preview from "./Preview";
import { addGrmCustomerPrescriptionFile } from "../../../api/grm";

const documentMap = [
  { key: "prescriptionRx", label: "Prescription (Rx)" },
  { key: "proofOfResidency", label: "Proof of Residency" },
  { key: "driversLicence", label: "Driver's License / Gov ID" },
  { key: "insuranceCard", label: "Insurance Card" },
  { key: "priorAuth", label: "Prior Auth Documentation" },
];

const RequiredDocuments = ({
  data,
  onDataChange,
  onValidationChange,
  onFilesChange,
  customerId,
  showErrors,
}) => {
  const [docs, setDocs] = useState(data);
  const [uploadError, setUploadError] = useState("");
  const fileInputRef = React.useRef(null);
  const [activeDoc, setActiveDoc] = useState(null);

  const [pendingFileData, setPendingFileData] = useState(null); // Stores { file, dimensions }
  const [pendingDocKey, setPendingDocKey] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);

  const [uploadedFiles, setUploadedFiles] = useState({}); // Stores { file, dimensions }
  const [reviewData, setReviewData] = useState(null);
  const [isUploading, setIsUploading] = useState(false);

  useEffect(() => {
    if (onFilesChange) {
      onFilesChange(uploadedFiles);
    }
  }, [uploadedFiles, onFilesChange]);

  useEffect(() => {
    setDocs(data);
  }, [data]);

  useEffect(() => {
    if (!pendingFileData?.file) {
      setPreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(pendingFileData.file);
    setPreviewUrl(url);
    return () => {
      URL.revokeObjectURL(url);
    };
  }, [pendingFileData]);

  const fixedKeys = new Set(documentMap.map((d) => d.key));
  const extraDocsKeys = Object.keys(docs || {}).filter(
    (key) => !fixedKeys.has(key),
  );
  const allDocs = [
    ...documentMap.map((doc) => ({ ...doc, status: docs[doc.key] })),
    ...extraDocsKeys.map((key) => ({
      key,
      label: typeof docs[key] === "object" ? docs[key].label : key,
      status: typeof docs[key] === "object" ? docs[key].status : docs[key],
    })),
  ];

  useEffect(() => {
    if (onValidationChange) {
      const error = validateDocuments(docs);
      onValidationChange(error === null);
    }
  }, [docs, onValidationChange]);

  const getDocColor = (status) => {
    if (status === "Uploaded") return "#3DA97A";
    if (status === "Required") return "#D97706";
    if (status === "Optional") return "#9F9DB6";
    return "#9F9DB6";
  };

  const handleDocClick = (key, status, label) => {
    if (status === "Uploaded") {
      const fileDataObj = uploadedFiles[key];
      if (fileDataObj) {
        const url = URL.createObjectURL(fileDataObj.file);
        setReviewData({
          url,
          name: fileDataObj.file.name,
          isLocal: true,
          dimensions: fileDataObj.dimensions,
        });
      } else {
        // For remote files, we are currently only using a dummy PDF.
        // Ensure the name reflects a PDF for consistent preview logic.
        const displayName = `${(label || key).split(".").slice(0, -1).join(".") || label || key}.pdf`;
        setReviewData({
          url: "https://www.w3.org/WAI/ER/tests/xhtml/testfiles/resources/pdf/dummy.pdf",
          name: displayName,
          isLocal: false,
          dimensions: null,
        });
      }
    } else if (
      status === "Required" ||
      status === "Optional" ||
      typeof status !== "string"
    ) {
      setActiveDoc(key);
      fileInputRef.current.click();
    }
  };

  const handleCloseReview = () => {
    if (reviewData?.isLocal && reviewData?.url) {
      URL.revokeObjectURL(reviewData.url);
    }
    setReviewData(null);
  };

  const handleAddNewDoc = () => {
    setActiveDoc("__NEW__");
    fileInputRef.current.click();
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadError("");
    const allowedExtensions = ["pdf", "png", "jpg", "jpeg", "gif"];
    const extension = file.name.split(".").pop().toLowerCase();

    if (!allowedExtensions.includes(extension)) {
      setUploadError("File data must be in GIF, JPEG, PDF or PNG format");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setUploadError("File size exceeds 5MB limit");
      return;
    }

    const isImage = ["png", "jpg", "jpeg", "gif"].includes(extension);
    if (isImage) {
      const img = new Image();
      const tempUrl = URL.createObjectURL(file);
      img.onload = () => {
        setPendingFileData({
          file,
          dimensions: { width: img.width, height: img.height },
        });
        URL.revokeObjectURL(tempUrl);
      };
      img.src = tempUrl;
    } else {
      setPendingFileData({ file, dimensions: null });
    }

    setPendingDocKey(activeDoc);
    e.target.value = null; // Reset file input
  };

  const getGrmFileType = (key) => {
    switch (key) {
      case "prescriptionRx":
        return "Prescription";
      case "proofOfResidency":
        return "Proof of Residency";
      case "driversLicence":
        return "Driving License";
      case "insuranceCard":
        return "Insurance Card";
      case "priorAuth":
        return "Prior Auth";
      default:
        return "Other";
    }
  };

  const handleConfirmUpload = () => {
    if (!pendingFileData?.file) return;

    const file = pendingFileData.file;
    const reader = new FileReader();

    setIsUploading(true);
    setUploadError("");

    reader.onload = async () => {
      try {
        const base64Data = reader.result.split(",")[1];
        const fileTypeMapped = getGrmFileType(pendingDocKey);

        const payload = {
          fileName: file.name,
          fileType: fileTypeMapped,
          data: base64Data,
        };

        const resolvedCustomerId = customerId || 101802;
        await addGrmCustomerPrescriptionFile(resolvedCustomerId, payload);
        console.log("Uploaded file successfully to GRM");

        let updatedDocs;
        let newKey = pendingDocKey;
        if (pendingDocKey === "__NEW__") {
          newKey = `extra_${Date.now()}`;
          updatedDocs = {
            ...docs,
            [newKey]: {
              status: "Uploaded",
              label: pendingFileData.file.name,
            },
          };
        } else {
          updatedDocs = { ...docs, [pendingDocKey]: "Uploaded" };
        }

        setUploadedFiles((prev) => ({
          ...prev,
          [newKey]: pendingFileData,
        }));

        setDocs(updatedDocs);
        onDataChange(updatedDocs);
        setPendingFileData(null);
        setPendingDocKey(null);
        setActiveDoc(null);
      } catch (error) {
        console.error("Failed to upload file to GRM:", error);
        setUploadError("Failed to upload file. Please try again.");
      } finally {
        setIsUploading(false);
      }
    };

    reader.onerror = () => {
      console.error("FileReader error");
      setUploadError("Failed to read file.");
      setIsUploading(false);
    };

    reader.readAsDataURL(file);
  };

  const handleCancelUpload = () => {
    if (isUploading) return;
    setUploadError("");
    setPendingFileData(null);
    setPendingDocKey(null);
    setActiveDoc(null);
  };

  return (
    <Box
      sx={{
        backgroundColor: "#FFFFFF",
        borderRadius: "12px",
        p: 2,
        border: "1px solid #E8E8E9",
        display: "flex",
        flexDirection: "column",
        gap: 2,
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          width: "100%",
        }}
      >
        <Typography
          sx={{ fontWeight: 600, fontSize: "16px", color: "#403C6F" }}
        >
          Required Documents
        </Typography>
        {uploadError && (
          <Typography
            sx={{
              color: "#E53935",
              fontSize: "12px",
              fontWeight: 500,
            }}
          >
            {uploadError}
          </Typography>
        )}
      </Box>

      <input
        type="file"
        ref={fileInputRef}
        style={{ display: "none" }}
        onChange={handleFileChange}
        accept=".pdf,.png,.jpg,.jpeg,.gif"
      />

      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
        {allDocs.map((doc) => (
          <Box
            key={doc.key}
            onClick={() => handleDocClick(doc.key, doc.status, doc.label)}
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              border: "1px solid #E8E8E9",
              borderRadius: "8px",
              p: 1.5,
              position: "relative",
              overflow: "hidden",
              cursor:
                doc.status === "Required" ||
                doc.status === "Optional" ||
                doc.status === "Uploaded"
                  ? "pointer"
                  : "default",
              "&:hover": {
                backgroundColor:
                  doc.status === "Required" ||
                  doc.status === "Optional" ||
                  doc.status === "Uploaded"
                    ? "#F9FAFB"
                    : "transparent",
              },
            }}
          >
            <Box
              sx={{
                position: "absolute",
                left: 0,
                top: 0,
                bottom: 0,
                width: "4px",
                backgroundColor: getDocColor(doc.status),
              }}
            />
            <Typography
              sx={{
                fontSize: "14px",
                color: "#10100F",
                ml: 1,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                mr: 2,
              }}
            >
              {doc.label}
            </Typography>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                flexShrink: 0,
              }}
            >
              {doc.status === "Optional" ? (
                <AddIcon
                  sx={{
                    color: getDocColor(doc.status),
                    fontSize: "20px",
                  }}
                />
              ) : (
                <Typography
                  sx={{
                    fontSize: "14px",
                    color: getDocColor(doc.status),
                    fontWeight: 500,
                    mr: 0,
                  }}
                >
                  {doc.status}
                </Typography>
              )}
            </Box>
          </Box>
        ))}
      </Box>

      <Button
        onClick={handleAddNewDoc}
        startIcon={<AddIcon />}
        variant="outlined"
        sx={{
          width: "40%",
          justifyContent: "center",
          textTransform: "none",
          fontWeight: 500,
          fontSize: "14px",
          color: "#403C6F",
          borderColor: "#9F9DB6",
          borderRadius: "50px",
          px: 3,
          py: 1,
          mt: 1,
          "&:hover": {
            backgroundColor: "rgba(64, 60, 111, 0.04)",
            borderColor: "#403C6F",
          },
        }}
      >
        Upload document
      </Button>

      {showErrors && validateDocuments(docs) && (
        <Typography
          sx={{ color: "#E53935", fontSize: "12px", mt: 1, fontWeight: 500 }}
        >
          {validateDocuments(docs)}
        </Typography>
      )}

      {pendingFileData && (
        <Dialog
          open={Boolean(pendingFileData)}
          onClose={handleCancelUpload}
          maxWidth="md"
          fullWidth
          slotProps={{
            paper: {
              sx: {
                borderRadius: "16px",
                p: 2,
                maxHeight: "90vh",
                display: "flex",
                flexDirection: "column",
              },
            },
          }}
        >
          <DialogTitle
            sx={{
              p: 1,
              fontWeight: 700,
              fontSize: "18px",
              color: "#10100F",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <span>Document Preview</span>
            <IconButton
              onClick={handleCancelUpload}
              disabled={isUploading}
              size="small"
              sx={{ color: "#9F9DB6", "&:hover": { color: "#E53935" } }}
            >
              <CloseIcon sx={{ fontSize: "20px" }} />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ p: 1, mt: 1 }}>
            <Box
              sx={{ display: "flex", flexDirection: "column", gap: 0.5, mb: 2 }}
            >
              <Typography
                sx={{ fontSize: "14px", fontWeight: 600, color: "#403C6F" }}
              >
                File:{" "}
                <span style={{ fontWeight: 400, color: "#10100F" }}>
                  {pendingFileData.file.name}
                </span>
              </Typography>
              <Typography sx={{ fontSize: "12px", color: "#9F9DB6" }}>
                Size: {(pendingFileData.file.size / (1024 * 1024)).toFixed(2)}{" "}
                MB
              </Typography>
              {pendingFileData.dimensions && (
                <Typography sx={{ fontSize: "12px", color: "#9F9DB6" }}>
                  Dimensions: {pendingFileData.dimensions.width} x{" "}
                  {pendingFileData.dimensions.height} px
                </Typography>
              )}
            </Box>
            <Preview
              fileUrl={previewUrl}
              fileName={pendingFileData.file.name}
            />
            {uploadError && (
              <Typography
                sx={{
                  color: "#E53935",
                  fontSize: "14px",
                  mt: 2,
                  fontWeight: 500,
                  textAlign: "center",
                }}
              >
                {uploadError}
              </Typography>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 1, gap: 1.5 }}>
            <Button
              onClick={handleCancelUpload}
              disabled={isUploading}
              variant="outlined"
              sx={{
                borderRadius: "50px",
                textTransform: "none",
                fontWeight: 500,
                fontSize: "14px",
                color: "#403C6F",
                borderColor: "#9F9DB6",
                px: 3,
                py: 1,
                "&:hover": {
                  backgroundColor: "rgba(64, 60, 111, 0.04)",
                  borderColor: "#403C6F",
                },
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmUpload}
              disabled={isUploading}
              variant="contained"
              sx={{
                borderRadius: "50px",
                textTransform: "none",
                fontWeight: 500,
                fontSize: "14px",
                backgroundColor: "#403C6F",
                color: "#FFF",
                px: 4,
                py: 1,
                boxShadow: "none",
                "&:hover": {
                  backgroundColor: "#312e5e",
                  boxShadow: "none",
                },
              }}
            >
              {isUploading ? "Uploading..." : "Upload"}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {reviewData && (
        <Dialog
          open={Boolean(reviewData)}
          onClose={handleCloseReview}
          maxWidth="sm"
          fullWidth
          slotProps={{
            paper: {
              sx: {
                borderRadius: "16px",
                p: 2,
              },
            },
          }}
        >
          <DialogTitle
            sx={{
              p: 1,
              fontWeight: 700,
              fontSize: "18px",
              color: "#10100F",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <span>Document Review</span>
            <IconButton
              onClick={handleCloseReview}
              size="small"
              sx={{ color: "#9F9DB6", "&:hover": { color: "#E53935" } }}
            >
              <CloseIcon sx={{ fontSize: "20px" }} />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ p: 1, mt: 1 }}>
            <Box
              sx={{ display: "flex", flexDirection: "column", gap: 0.5, mb: 2 }}
            >
              <Typography
                sx={{ fontSize: "14px", fontWeight: 600, color: "#403C6F" }}
              >
                File:{" "}
                <span style={{ fontWeight: 400, color: "#10100F" }}>
                  {reviewData.name}
                </span>
              </Typography>
              {reviewData.dimensions && (
                <Typography sx={{ fontSize: "12px", color: "#9F9DB6" }}>
                  Dimensions: {reviewData.dimensions.width} x{" "}
                  {reviewData.dimensions.height} px
                </Typography>
              )}
            </Box>
            <Preview fileUrl={reviewData.url} fileName={reviewData.name} />
          </DialogContent>
          <DialogActions sx={{ p: 1 }}>
            <Button
              onClick={handleCloseReview}
              variant="contained"
              sx={{
                borderRadius: "50px",
                textTransform: "none",
                fontWeight: 500,
                fontSize: "14px",
                backgroundColor: "#403C6F",
                color: "#FFF",
                px: 4,
                py: 1,
                boxShadow: "none",
                "&:hover": {
                  backgroundColor: "#312e5e",
                  boxShadow: "none",
                },
              }}
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
};

RequiredDocuments.propTypes = {
  data: PropTypes.object.isRequired,
  onDataChange: PropTypes.func.isRequired,
  onValidationChange: PropTypes.func,
  onFilesChange: PropTypes.func,
  customerId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  showErrors: PropTypes.bool,
};

export default RequiredDocuments;
